// Native preset apply/reset/detach helpers. A preset is a bundle of the canonical
// caption style (lib/style.js STYLE_FIELDS) + an animation type (+ optional two-font
// config). Applying REPLACES the complete preset-controlled visual state so switching
// Preset A -> Preset B never leaks A's outline/shadow/glow/etc. Preview and export read
// the SAME canonical state (lib/style.js mirrors backend animation_engine).

// Explicit defaults for every effect TOGGLE + its params. Applying a preset always
// stamps these so an effect the new preset does NOT enable is deterministically OFF
// (fixes the "previous preset's outline/shadow stays on" bug). Fields the preset does
// not control (fontSize, opacity, position, scale) are intentionally omitted so they
// keep inheriting the caption's own values.
const EFFECT_DEFAULTS = {
  fillColor: "#FFFFFF", fontFamily: "Montserrat", fontWeight: 700, fontStyle: "normal",
  strokeEnabled: false, strokeColor: "#000000", strokeWidth: 6,
  shadowEnabled: false, shadowColor: "#000000", shadowDx: 3, shadowDy: 4, shadowBlur: 4, shadowOpacity: 0.6,
  glowEnabled: false, glowColor: "#00F0FF", glowBlur: 16, glowOpacity: 0.85,
  threeDEnabled: false, threeDColor: "#000000", threeDDepth: 6,
  bgEnabled: false, bgType: "solid", bgColor: "#000000", bgColor2: "#8B5CF6", bgAngle: 90,
  bgOpacity: 0.6, bgPaddingX: 0.35, bgPaddingY: 0.2, bgRadius: 0.35, bgMode: "container",
  fillGradientEnabled: false, fillGradientFrom: "#00F0FF", fillGradientTo: "#8B5CF6", fillGradientAngle: 90,
  align: "center", textCase: "none", letterSpacing: 0,
};

// Complete, normalized style for a preset — every preset-controlled field explicit.
export const normalizePresetStyle = (defStyle = {}) => {
  const out = { ...EFFECT_DEFAULTS };
  for (const [k, v] of Object.entries(defStyle)) if (v !== undefined && v !== null) out[k] = v;
  return out;
};

// Two-font config the preset controls at the (global) config level, mirroring the
// existing two-font system (config.fontMode + fontFamilyB) that both preview and
// export already read. Returned only when the preset explicitly defines a mode.
export const presetConfigPatch = (preset) => {
  const def = preset?.definition || {};
  if (!def.fontMode || def.fontMode === "single" || !def.fontFamilyB) return null;
  const s = def.style || {};
  return {
    fontMode: def.fontMode,
    fontFamily: s.fontFamily || "Montserrat",
    fontWeight: s.fontWeight || 700,
    fontFamilyB: def.fontFamilyB,
    fontWeightB: def.fontWeightB || s.fontWeight || 700,
    fontStyleB: def.fontStyleB || "normal",
  };
};

// Fonts a preset showcases, for the card/detail preview (actual fonts, not generic).
export const presetFonts = (def = {}) => {
  const s = def.style || {};
  const primary = { family: s.fontFamily || "Montserrat", weight: s.fontWeight || 800, style: s.fontStyle || "normal" };
  const secondary = def.fontMode && def.fontMode !== "single" && def.fontFamilyB
    ? { family: def.fontFamilyB, weight: def.fontWeightB || s.fontWeight || 700, style: def.fontStyleB || "normal" }
    : null;
  return { primary, secondary, mode: def.fontMode || "single" };
};

export const applyPresetToObject = (obj, preset) => {
  const def = preset.definition || {};
  return {
    ...obj,
    style: normalizePresetStyle(def.style || {}),
    animation: def.animation || obj.animation || null,
    fx: def.fx || null,
    preset: {
      id: preset.id,
      version: preset.version || 1,
      name: preset.name,
      category: preset.category,
      definition: def,
    },
  };
};

// Reset patch = re-apply the stamped preset definition, dropping user overrides.
export const resetPresetPatch = (obj) => {
  const def = obj?.preset?.definition || {};
  return { style: normalizePresetStyle(def.style || {}), animation: def.animation || obj.animation || null };
};

// Base font size a phrase pattern's sizeScale is relative to.
export const baseFontSizeOf = (obj, config) => Number(obj?.style?.fontSize || config?.fontSize || 80);

// Phrase Designs: word i of each caption gets wordPattern[i % n] (font, size, colour,
// background, animation…). Writes config.wordStyles — the per-word layer preview + export read.
// Always clears the target captions' old per-word overrides so a new preset fully replaces them.
const SENTENCE_END = /[.!?。！？।॥…]["'”’)\]]*$/;
const SENTENCE_GAP = 0.8;
const wordsOf = (o) => (o.words && o.words.length ? o.words : [{ word: o.text, word_id: o.word_id }]);
// One look per caption layer: layerObjs[i] (layer i+1, priority order) gets pattern[i % n] on every word.
export const layerPatternPatch = (layerObjs, def, wordStyles = {}, config = {}) => {
  const pat = def?.wordPattern || [];
  const next = { ...wordStyles };
  layerObjs.forEach((objs, li) => objs.forEach((o) => wordsOf(o).forEach((w) => {
    if (!w.word_id) return;
    const p = { ...pat[li % pat.length] };
    const sc = p.sizeScale; delete p.sizeScale;
    if (sc) p.fontSize = Math.round(baseFontSizeOf(o, config) * sc);
    next[w.word_id] = p;
  })));
  return next;
};
// word_ids that end a sentence, read from the transcript text (word tokens often lack punctuation).
export const sentenceEndsFromSegments = (segments = []) => {
  const out = new Set();
  segments.forEach((seg) => {
    const ws = seg.words || [];
    if (!ws.length) return;
    const toks = String(seg.text || "").trim().split(/\s+/).filter(Boolean);
    if (toks.length === ws.length) toks.forEach((t, i) => { if (SENTENCE_END.test(t) && ws[i].word_id) out.add(ws[i].word_id); });
    if (SENTENCE_END.test(String(seg.text || "").trim()) && ws[ws.length - 1].word_id) out.add(ws[ws.length - 1].word_id);
  });
  return out;
};
export const phrasePatternPatch = (objs, def, wordStyles = {}, config = {}, sentenceEnds = null) => {
  const pat = def?.wordPattern || [];
  const next = { ...wordStyles };
  const sorted = [...objs].sort((a, b) => (a.start || 0) - (b.start || 0));
  sorted.forEach((o) => wordsOf(o).forEach((w) => { if (w.word_id) delete next[w.word_id]; }));
  if (!pat.length) return next;
  // Pattern restarts at each sentence: sentence punctuation, a pause, or a new multi-word caption.
  let run = 0, prevEnd = null;
  sorted.forEach((o) => {
    const words = wordsOf(o);
    if (words.length > 1 || (prevEnd != null && (o.start || 0) - prevEnd > SENTENCE_GAP)) run = 0;
    words.forEach((w) => {
      const idx = run++;
      if (SENTENCE_END.test(String(w.word || "").trim()) || (sentenceEnds && sentenceEnds.has(w.word_id))) run = 0;
      if (!w.word_id) return;
      const p = { ...pat[idx % pat.length] };
      const sc = p.sizeScale; delete p.sizeScale;
      if (sc) p.fontSize = Math.round(baseFontSizeOf(o, config) * sc);
      next[w.word_id] = p;
    });
    if (words.length > 1) run = 0;
    prevEnd = o.end || 0;
  });
  return next;
};

// Build a preset definition from a caption's current look (for "Save as New Preset").
// Per-word designs are captured as a position pattern so they can be re-applied anywhere.
export const definitionFromObject = (obj, wordStyles = {}, config = {}) => {
  const def = { style: { ...(obj.style || {}) }, animation: obj.animation || "pop" };
  if (obj.fx) def.fx = obj.fx;
  const words = obj.words || [];
  const base = baseFontSizeOf(obj, config);
  const pat = words.map((w) => {
    const ov = { ...(wordStyles[w.word_id] || {}) };
    if (ov.fontSize) { ov.sizeScale = Math.round((ov.fontSize / base) * 100) / 100; delete ov.fontSize; }
    return ov;
  });
  if (pat.some((p) => Object.keys(p).length)) {
    def.wordPattern = pat;
    def.sample = words.map((w) => w.word).join(" ").slice(0, 60);
  }
  if (config.fontMode && config.fontMode !== "single" && config.fontFamilyB) {
    Object.assign(def, { fontMode: config.fontMode, fontFamilyB: config.fontFamilyB, fontWeightB: config.fontWeightB, fontStyleB: config.fontStyleB });
  }
  return def;
};

// Inline CSS to preview a preset's style on a card (approximation of the canvas render).
export const previewCss = (def = {}, fontOverride = null) => {
  const s = def.style || {};
  const css = {
    fontFamily: `"${fontOverride?.family || s.fontFamily || "Montserrat"}", sans-serif`,
    fontWeight: fontOverride?.weight || s.fontWeight || 800,
    fontStyle: fontOverride?.style || s.fontStyle || "normal",
    color: s.fillColor || "#FFFFFF",
  };
  if (s.fillGradientEnabled) {
    css.backgroundImage = `linear-gradient(${s.fillGradientAngle ?? 90}deg, ${s.fillGradientFrom || "#00F0FF"}, ${s.fillGradientTo || "#8B5CF6"})`;
    css.WebkitBackgroundClip = "text"; css.backgroundClip = "text"; css.WebkitTextFillColor = "transparent"; css.color = "transparent";
  }
  const shadows = [];
  if (s.strokeEnabled !== false && s.strokeColor && (s.strokeWidth || 0) > 0) {
    const w = Math.max(1, Math.round((s.strokeWidth || 4) / 3));
    css.WebkitTextStroke = `${w}px ${s.strokeColor}`;
  }
  if (s.shadowEnabled) shadows.push(`${(s.shadowDx ?? 2)}px ${(s.shadowDy ?? 3)}px ${(s.shadowBlur ?? 6)}px rgba(0,0,0,${s.shadowOpacity ?? 0.6})`);
  if (s.glowEnabled) shadows.push(`0 0 ${(s.glowBlur ?? 16)}px ${s.glowColor || "#00F0FF"}`);
  if (shadows.length) css.textShadow = shadows.join(", ");
  return css;
};

export const previewBgCss = (def = {}) => {
  const s = def.style || {};
  if (!s.bgEnabled) return null;
  if (s.bgType === "gradient") return { backgroundImage: `linear-gradient(${s.bgAngle ?? 90}deg, ${s.bgColor || "#000"}, ${s.bgColor2 || "#8B5CF6"})`, opacity: s.bgOpacity ?? 0.9 };
  return { backgroundColor: s.bgColor || "#000", opacity: s.bgOpacity ?? 0.6 };
};
